import first from 'lodash/first';
import uniq from 'lodash/uniq';
import type { OnlinePlannerSubject } from '../../Interfaces/OnlinePlannerSubject';
import type { RestrictedSingleSubject } from '../../Interfaces/RestrictedSingleSubject';
import type { OnlineApplicationChoice } from '~/Features/OnlineApplication/Model';
import { TimeType } from '~/Pages/OnlineStudyPage/Semester/ParentStudyViewModel';
import type {
    PlannerBookmark,
    PlannerFiltersLabels,
    PlannerFilterType,
    PlannerSelectedSubjects,
    PlannerSubjectsGroupingType,
    PlannerTabsLabels,
    SelectedSubject,
    SemesterNumber,
} from './Context/types';

export const mapOnlinePlannerSubjectToSubjectBase = (subject: OnlinePlannerSubject): RestrictedSingleSubject => ({
    subjectName: subject.name,
    subjectCode: subject.code,
    subjectCategory: subject.subjectCategory,
    educationCategories: subject.educationCategories,
    points: subject.points,
    price: subject.price,
    linkUrl: subject.link?.href,
    electiveGroupCode: subject.electiveGroupCode,
    parentStudies: subject.parentStudies,
    canBePurchased: subject.canBePurchased,
    startDate: subject.startDate,
});

const getParentsStudy = (subject: SelectedSubject) =>
    subject.tab === 'fullTime' || (subject.tab === 'subjects' && subject.parentStudies?.fullTimeCode)
        ? {
              code: subject.parentStudies?.fullTimeCode ?? '',
              timeType: TimeType.fullTime,
          }
        : {
              code: subject.parentStudies?.partTimeCode ?? '',
              timeType: TimeType.partTime,
          };

export const mapPlannerSubjectToOnlineApplicationChoice = (
    subject: SelectedSubject,
    creditsLabel: string
): OnlineApplicationChoice => ({
    subject: {
        name: subject.subjectName,
        code: subject.subjectCode,
        educationCategories: subject.educationCategories,
        price: subject.price,
        creditsLabel,
        credits: subject.points.amount,
        url: subject.linkUrl ?? '',
        startDate: subject.startDate?.toISOString(),
    },
    parentStudy: getParentsStudy(subject),
});

export const getBookmarks = (subjects: OnlinePlannerSubject[], tabs: PlannerTabsLabels): PlannerBookmark[] => {
    const bookmarks: PlannerBookmark[] = [];

    const hasPartTimeSubject = subjects.find(subject => subject.semesterDefinition.partTime) !== undefined;

    if (hasPartTimeSubject) {
        bookmarks.push({
            uniqueId: 0,
            label: tabs.partTime,
            contextKey: 'partTime',
        });
    }

    const hasFullTimeSubject = subjects.find(subject => subject.semesterDefinition.fullTime) !== undefined;

    if (hasFullTimeSubject) {
        bookmarks.push({
            uniqueId: 1,
            label: tabs.fullTime,
            contextKey: 'fullTime',
        });
    }

    bookmarks.push({
        uniqueId: 2,
        label: tabs.singleSubjects,
        contextKey: 'subjects',
    });

    return bookmarks;
};

export const getFilters = (
    subjects: OnlinePlannerSubject[],
    filters: PlannerFiltersLabels
): Map<PlannerFilterType, string> => {
    const filterOptions = new Map<PlannerFilterType, string>();

    filterOptions.set('all', filters.allSubjects);
    filterOptions.set('obligatory', filters.obligatory);

    const hasSpecializationSubject =
        subjects.find(subject => subject.subjectCategory === 'specialization') !== undefined;

    if (hasSpecializationSubject) {
        filterOptions.set('specialization', filters.specialization);
    }

    const hasElectiveSubject = subjects.find(subject => subject.subjectCategory === 'elective') !== undefined;

    if (hasElectiveSubject) {
        filterOptions.set('elective', filters.elective);
    }

    return filterOptions;
};

export const checkIfSubjectIsSelected = (
    subject: RestrictedSingleSubject,
    tab: PlannerSubjectsGroupingType,
    semester: SemesterNumber,
    selectedSubjects: PlannerSelectedSubjects
) => {
    const selectedSubject = selectedSubjects.get(subject.subjectCode);

    if (!selectedSubject) {
        return false;
    }

    return selectedSubject.tab === tab && selectedSubject.semester === semester;
};

export const getSelectedSubjectsTab = (subjects: PlannerSelectedSubjects) => {
    const selectedTabs = uniq(Array.from(subjects.values()).map(selectedSubject => selectedSubject.tab));

    if (selectedTabs.length > 1) {
        throw new Error('There must be only one tab among selected subjects');
    }

    return first(selectedTabs);
};
